import "./cancel-shipment-modal.css";

import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Error, Hint } from "@tql/ui-components-react/kendo/labels";
import { useEffect, useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostVoidShipment } from "../../hooks/use-post-void-shipment.ts";
import { CancelDispatchErrorModal } from "../cancel-dispatch-error-modal/index.tsx";

const CANCEL_REASON_MAX_LENGTH = 200;

export const CancelShipmentModal = ({
	carrierPhone,
	onClose,
	isOpen,
}: {
	carrierPhone?: string | null;
	onClose: () => void;
	isOpen: boolean;
}) => {
	const confirmationNumberId = useId();
	const errorId = `${confirmationNumberId}_error`;
	const countId = `${confirmationNumberId}_count`;
	const [showError, setShowError] = useState(false);
	const [cancelReasonMessage, setCancelReasonMessage] = useState("");
	const params = useParams();
	const [dispatchModalState, setDispatchModalState] = useState<{
		confirmationNumber: string | null | undefined;
	} | null>(null);
	const { voidShipmentAsync, isSavingVoidShipment } = usePostVoidShipment();

	// Match the 15s auto-close used by toast error notifications elsewhere.
	useEffect(() => {
		if (!showError) return;
		const timer = setTimeout(() => setShowError(false), 15_000);
		return () => clearTimeout(timer);
	}, [showError]);

	const handleChange = (event: TextAreaChangeEvent) => {
		if (showError) {
			setShowError(false);
		}
		setCancelReasonMessage(event.value);
	};

	const handleConfirm = async () => {
		if (isSavingVoidShipment) {
			return;
		}
		if (!cancelReasonMessage.trim()) {
			setShowError(true);
			return;
		}

		try {
			const resp = await voidShipmentAsync({
				poNumber: Number(params["poNumber"]),
				reason: cancelReasonMessage,
			});
			setCancelReasonMessage("");

			// The carrier dispatch could not be cancelled automatically — surface
			// the cancel-dispatch modal so the user knows to call the carrier.
			if (
				resp.carrierDispatchCancelled === false &&
				resp.shipmentStateAtVoid &&
				resp.shipmentStateAtVoid > 0
			) {
				setDispatchModalState({ confirmationNumber: resp.confirmationNumber });
				return;
			}

			onClose();
		} catch {
			// The void mutation surfaces its own error notification.
			onClose();
		}
	};

	if (!isOpen) {
		return null;
	}

	// The carrier dispatch could not be cancelled automatically — replace the
	// cancel-shipment form with the cancel-dispatch modal so the user knows to
	// call the carrier.
	if (dispatchModalState !== null) {
		return (
			<CancelDispatchErrorModal
				carrierPhone={carrierPhone}
				confirmationNumber={dispatchModalState.confirmationNumber}
				isOpen={true}
				onClose={() => {
					setDispatchModalState(null);
					onClose();
				}}
			/>
		);
	}

	return (
		<TraxModal
			cancelText={"Don't cancel"}
			className={"cancel-shipment-modal"}
			confirmText={"Cancel shipment"}
			onCancel={() => {
				setCancelReasonMessage("");
				onClose();
			}}
			onClose={() => {
				setCancelReasonMessage("");
				onClose();
			}}
			onConfirm={() => void handleConfirm()}
			title={`Cancel Shipment`}
			type={"confirmation"}
			width={397}
		>
			<div className={"cancel-shipment-field"}>
				<label htmlFor={confirmationNumberId}>
					Please enter a reason for canceling this shipment.
					<span className={"required"}>&nbsp;*</span>
				</label>
				<TextArea
					aria-invalid={showError}
					ariaDescribedBy={showError ? `${errorId} ${countId}` : countId}
					className={"cancel-shipment-reason"}
					id={confirmationNumberId}
					maxLength={CANCEL_REASON_MAX_LENGTH}
					onChange={handleChange}
					valid={!showError}
					value={cancelReasonMessage}
				/>
				<div className={"cancel-shipment-meta"}>
					{showError ? (
						<Error id={errorId}>Cancellation reason is required.</Error>
					) : null}
					<Hint
						className={"cancel-shipment-count"}
						direction={"end"}
						id={countId}
					>
						{cancelReasonMessage.length}/{CANCEL_REASON_MAX_LENGTH}
					</Hint>
				</div>
			</div>
		</TraxModal>
	);
};
